import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/shared/state/authstore'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { chatApi } from '../api/client'
import { chatKeys } from './keys'
import { useChatUiStore } from './chatUiStore'
import { flushDeliveredForList } from './deliveredQueue'
import { useChatCapabilities } from './chatCapabilities'
import type { ChatConversation } from '../api/types'

export function useChatAccountId(): number | null {
  return useAuthStore((state) => state.accountId)
}

export function useChatUserId(): number | null {
  return useAuthStore((state) => state.userId)
}

export function useChatEnabled(): boolean {
  const accountId = useChatAccountId()
  const accessToken = useAuthStore((state) => state.accessToken)
  const accessEnded = useChatUiStore((state) => state.accessEnded)
  const { enabled } = useChatCapabilities()
  return Boolean(accessToken) && accountId !== null && !accessEnded && enabled
}

export function useConversations(options?: { poll?: boolean }) {
  const accountId = useChatAccountId()
  const enabled = useChatEnabled()
  const streamStatus = useChatUiStore((state) => state.streamStatus)
  const poll = options?.poll ?? false
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: chatKeys.conversations(accountId ?? 0),
    queryFn: async () => {
      const conversations = await chatApi.conversations()
      flushDeliveredForList(conversations)
      if (accountId !== null) {
        const previous = queryClient.getQueryData<ChatConversation[]>(chatKeys.conversations(accountId))
        for (const conversation of conversations) {
          const before = previous?.find((row) => row.id === conversation.id)
          if (before && before.title !== conversation.title) {
            void queryClient.invalidateQueries({ queryKey: chatKeys.conversation(accountId, conversation.id) })
          }
        }
      }
      return conversations
    },
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: poll ? (streamStatus === 'live' ? 60_000 : 30_000) : false,
    refetchOnWindowFocus: poll,
    retry: 1,
  })

  return query
}

export function useConversation(id: number | null) {
  const accountId = useChatAccountId()
  const enabled = useChatEnabled() && id !== null
  const streamStatus = useChatUiStore((state) => state.streamStatus)

  return useQuery({
    queryKey: chatKeys.conversation(accountId ?? 0, id ?? 0),
    queryFn: () => chatApi.conversation(id as number),
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: streamStatus === 'live' ? false : 30_000,
    retry: 1,
  })
}

export function useChatBadge(): number {
  const { data } = useConversations()
  return (data ?? []).filter((conversation) => conversation.hasUnread).length
}

export function useChatDirectory(search: string, enabled: boolean) {
  const accountId = useChatAccountId()
  const { canChat } = useChatCapabilities()
  const term = useDebounce(search, 300)

  return useQuery({
    queryKey: chatKeys.directory(accountId ?? 0, term),
    queryFn: () => chatApi.directory(term),
    enabled: enabled && canChat && accountId !== null,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    retry: 1,
  })
}

import { useInfiniteQuery, type QueryClient } from '@tanstack/react-query'
import { MESSAGE_PAGE_LIMIT } from '../api/client'
import type { ChatMessage } from '../api/types'

type MessagePageParam = { before?: number; after?: number }

export function useMessages(conversationId: number | null) {
  const accountId = useChatAccountId()
  const enabled = useChatEnabled() && conversationId !== null
  const streamStatus = useChatUiStore((state) => state.streamStatus)

  return useInfiniteQuery({
    queryKey: chatKeys.messages(accountId ?? 0, conversationId ?? 0),
    queryFn: ({ pageParam }) => chatApi.messages(conversationId as number, pageParam),
    initialPageParam: {} as MessagePageParam,
    getNextPageParam: (lastPage: ChatMessage[]): MessagePageParam | undefined =>
      lastPage.length < MESSAGE_PAGE_LIMIT || lastPage.length === 0 ? undefined : { before: lastPage[0].id },
    getPreviousPageParam: (firstPage: ChatMessage[]): MessagePageParam | undefined =>
      firstPage.length === 0 ? undefined : { after: firstPage[firstPage.length - 1].id },
    maxPages: 6,
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: streamStatus === 'live' ? false : 30_000,
    refetchOnWindowFocus: false,
    retry: 1,
  })
}

export function flattenMessages(pages: ChatMessage[][] | undefined): ChatMessage[] {
  if (!pages) return []
  return [...pages].reverse().flat()
}

export async function resyncThread(
  queryClient: QueryClient,
  accountId: number,
  conversationId: number,
): Promise<void> {
  const key = chatKeys.messages(accountId, conversationId)
  const cached = queryClient.getQueryData<{ pages: ChatMessage[][]; pageParams: MessagePageParam[] }>(key)
  const newest = cached?.pages?.[0]?.at(-1)?.id

  if (newest == null) {
    await queryClient.invalidateQueries({ queryKey: key })
    return
  }

  const page = await chatApi.messages(conversationId, { after: newest })

  if (page.length >= MESSAGE_PAGE_LIMIT) {
    queryClient.removeQueries({ queryKey: key })
    await queryClient.invalidateQueries({ queryKey: key })
    return
  }

  if (page.length === 0) return

  queryClient.setQueryData(key, (old: { pages: ChatMessage[][]; pageParams: MessagePageParam[] } | undefined) => {
    if (!old) return old
    const [first, ...rest] = old.pages
    const existing = new Set(first.map((message) => message.id))
    const appended = [...first, ...page.filter((message) => !existing.has(message.id))]
    return { ...old, pages: [appended, ...rest] }
  })
}

export function useQuotedMessages(conversationId: number | null, ids: number[]) {
  const accountId = useChatAccountId()
  const sorted = [...new Set(ids)].sort((a, b) => a - b).slice(0, 50)
  const enabled = useChatEnabled() && conversationId !== null && sorted.length > 0

  return useQuery({
    queryKey: chatKeys.quoted(accountId ?? 0, conversationId ?? 0, sorted),
    queryFn: () => chatApi.messagesByIds(conversationId as number, sorted),
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
    retry: 0,
  })
}

export function useAttachmentUrls(conversationId: number | null, fileIds: string[]) {
  const accountId = useChatAccountId()
  const sorted = [...new Set(fileIds)].sort().slice(0, 50)
  const enabled = useChatEnabled() && conversationId !== null && sorted.length > 0

  return useQuery({
    queryKey: chatKeys.attachmentUrls(accountId ?? 0, conversationId ?? 0, sorted),
    queryFn: () => chatApi.attachmentUrls(conversationId as number, sorted),
    enabled,
    staleTime: 12 * 60_000,
    gcTime: 15 * 60_000,
    retry: 0,
  })
}

export function useBroadcasts(clientId: number | null) {
  const accountId = useChatAccountId()
  const { canBroadcast } = useChatCapabilities()
  const enabled = useChatEnabled() && canBroadcast

  return useQuery({
    queryKey: chatKeys.broadcasts(accountId ?? 0, clientId),
    queryFn: () => chatApi.broadcasts(clientId),
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: 30_000,
    retry: 1,
  })
}

export function useBroadcast(id: number | null) {
  const accountId = useChatAccountId()
  const { canBroadcast } = useChatCapabilities()
  const enabled = useChatEnabled() && canBroadcast && id !== null

  return useQuery({
    queryKey: chatKeys.broadcast(accountId ?? 0, id ?? 0),
    queryFn: () => chatApi.broadcast(id as number),
    enabled,
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    refetchInterval: 30_000,
    retry: 1,
  })
}

export function useBroadcastShifts(enabled: boolean) {
  const accountId = useChatAccountId()
  const { canBroadcast } = useChatCapabilities()

  return useQuery({
    queryKey: chatKeys.broadcastShifts(accountId ?? 0),
    queryFn: () => chatApi.broadcastShifts(),
    enabled: useChatEnabled() && canBroadcast && enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 0,
  })
}

export function useShiftBroadcastReach(shiftId: number | null, guardUserIds: number[] | null) {
  const accountId = useChatAccountId()
  const { canBroadcast } = useChatCapabilities()
  const enabled = useChatEnabled() && canBroadcast && shiftId !== null

  return useQuery({
    queryKey: chatKeys.shiftReach(accountId ?? 0, shiftId ?? 0, guardUserIds),
    queryFn: () => chatApi.shiftBroadcastReach(shiftId as number, guardUserIds),
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 0,
  })
}

export function useBroadcastReach(clientId: number | null) {
  const accountId = useChatAccountId()
  const { canBroadcast } = useChatCapabilities()
  const enabled = useChatEnabled() && canBroadcast && clientId !== null

  return useQuery({
    queryKey: chatKeys.reach(accountId ?? 0, clientId ?? 0),
    queryFn: () => chatApi.broadcastReach(clientId as number),
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 0,
  })
}
